import type {
	Article,
	ArticleThumbnail,
	AspectRatio,
	ContentBlock,
	Image
} from '$lib/satellite.types';
import { decode } from 'js-base64';

import { blurhashToImageCssObject } from '@unpic/placeholder';

export const url = (article: Article | ArticleThumbnail): string => {
	return '/' + article.category?.slug + '/' + article.slug;
};

export const getGravatarUrl = (hash: string, size: number = 200): string => {
	return `https://img.attrnum.com/cdn-cgi/image/format=auto,width=${size}/g/${hash}/${size}`;
};

export const getBiggestImage = (images: Image[], preset: string): Image => {
	let image: Image = images[0];

	for (const img of images) {
		if (img.preset == preset && img.size[0] > image.size[0]) {
			image = img;
		}
	}

	return image;
};

export const srcHelper = (
	image: Image[],
	aspectRatio: AspectRatio
): { src: string; srcset: string } => {
	let src: string = '';
	const srcset: string[] = [];

	let maxSize = 0;

	for (const i of image) {
		if (i.preset !== aspectRatio) {
			continue;
		}

		if (i.size[0] > maxSize) {
			maxSize = i.size[0];
			src = i.url;
		}

		srcset.push(`${i.url} ${i.size[0]}w`);
	}

	return { src, srcset: srcset.join(', ') };
};

export const hydrate = (article: Article, inject: Record<number, string | null> = {}): string => {
	let currentParagraph = 0;

	const traverse = (blocks: ContentBlock[]): string => {
		const content: string[] = [];
		blocks.forEach((block) => {
			if (block.type === 'paragraph') {
				const externalIcon =
					'<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-4 h-4 inline-block m-1"><path stroke-linecap="round" stroke-linejoin="round" d="M13.5 6H5.25A2.25 2.25 0 0 0 3 8.25v10.5A2.25 2.25 0 0 0 5.25 21h10.5A2.25 2.25 0 0 0 18 18.75V10.5m-10.5 6L21 3m0 0h-5.25M21 3v5.25" /></svg>';
				const linkPattern = /<a\s+[^>]*href="(.*?)".*?>(.*?)<\/a>/gi;
				let text = block?.data?.text;

				text = text.replaceAll(linkPattern, (matched: string, url: string, text: string) => {
					return (
						'<a href="' +
						url +
						'" target="_blank" rel="noopener noreferrer">' +
						text +
						'<sup>' +
						externalIcon +
						'</sup></a>'
					);
				});

				content.push(`<p>${text}</p>`);
				currentParagraph++;

				if (inject[currentParagraph] && inject[currentParagraph] !== null) {
					content.push(inject[currentParagraph] ?? '');
				}

				return;
			}

			if (block.type == 'list') {
				const prepare: string[] = [];
				prepare.push(block.ordered ? '<ol>' : '<ul>');
				block?.data?.forEach((listItem: string) => {
					prepare.push(`<li>${listItem}</li>`);
				});
				prepare.push(block.ordered ? '</ol>' : '</ul>');
				content.push(prepare.join(''));
				return;
			}

			if (block.type == 'delimiter') {
				content.push('<hr />');
				return;
			}

			if (block.type == 'code') {
				content.push(`<pre><code>${block?.data?.text}</code></pre>`);
				return;
			}

			if (block.type == 'header') {
				const level: number = block?.data?.level ?? 1;
				content.push(`<h${level}>${block?.data?.text}</h${level}>`);
				return;
			}

			if (block.type == 'quote') {
				let quote = traverse(block?.data?.content);
				quote = quote.replace(/<p><cite>(.*?)<\/cite><\/p>/i, '<cite>$1</cite>');
				content.push(`<blockquote>${quote}</blockquote>`);
				return;
			}

			if (block.type == 'layer') {
				content.push(`<div>${traverse(block?.data?.content)}</div>`);
				return;
			}

			if (block.type == 'columns') {
				content.push(
					`<div class="grid grid-cols-12 gap-1">${traverse(block?.data?.content)}</div>`
				);
				return;
			}

			if (block.type == 'column') {
				const size: number = block?.data?.size ?? 12;
				content.push(
					`<div class="col-span-12 lg:col-span-${size}">${traverse(block?.data?.content)}</div>`
				);
				return;
			}

			if (block.type == 'embed') {
				content.push(`<figure>${block?.data?.content}</figure>`);
				return;
			}

			if (block.type == 'hyperembed') {
				let insideContent = block?.data?.content;
				const isBrightcove =
					insideContent.match(/interstellar\.attrnum\.com\/qloader\.js/i) ||
					insideContent.match(/players\.brightcove\.net/i);

				if (!isBrightcove) {
					const pattern = /<script.*?<\/script>/gi;
					insideContent = block?.data?.content?.replace(pattern, '');
				}
				content.push(`<figure class="not-prose" data-hyperembed>${insideContent}</figure>`);
				return;
			}

			if (block.type == 'image') {
				const src: string | null = block?.data?.src || null;
				const alt: string | null = block?.data?.alt || null;
				const id: string | null = block?.data?.id || null;
				const aspect: string | null = block?.data?.aspect || null;
				const blurhash: string | null = block?.data?.blurhash || null;
				const caption: string | null = block?.data?.caption || null;
				const srcset: { [key: string]: string }[] | null = block?.data?.srcset || null;
				const link: string | null = block?.data?.link || null;

				const attributes: string[] = ['img'];
				const style: string[] = [];
				const classes: string[] = ['w-full', 'rounded-lg'];
				const figureAttributes: string[] = ['class="w-full"'];

				if (src !== null) {
					attributes.push(`src="${src}"`);
				}

				if (alt !== null) {
					attributes.push(`alt="${alt}"`);
				} else {
					attributes.push(`alt="${article.title}"`);
				}

				if (id !== null) {
					attributes.push(`data-image="${id}"`);
				}

				if (aspect !== null) {
					classes.push('w-full', 'h-full', 'object-cover');
					style.push(`aspect-ratio: ${aspect.replace(':', '/')};`);
				}

				if (blurhash !== null) {
					const decoded = decode(blurhash);
					const obj = blurhashToImageCssObject(decoded);
					classes.push('bg-cover');
					style.push(`background-image: ${obj.backgroundImage.replaceAll('"', '')};`);
				}

				attributes.push('loading="lazy"');
				attributes.push(
					'sizes="(max-width: 511px) 100vw, (min-width: 512px) 512px, (min-width: 1024px) 668px, (min-width: 1280px) 700px, 100vw"'
				);

				if (srcset !== null) {
					const srcsetAttribute: string[] = [];

					Object.entries(srcset).forEach((entry) => {
						const [, value] = entry;
						srcsetAttribute.push(`${value.url} ${value.size[0]}w`);
					});

					attributes.push(`srcset="${srcsetAttribute.join(', ')}"`);
				}

				if (classes.length > 0) {
					attributes.push(`class="${classes.join(' ')}"`);
				}

				if (style.length > 0) {
					attributes.push(`style="${style.join(' ')}"`);
				}

				let img = `<${attributes.join(' ')}>`;

				if (link !== null) {
					img = `<a href="${link}" target="_blank" rel="noopener noreferrer">${img}</a>`;
				}

				if (caption !== null) {
					img = `${img}<figcaption class="mt-1 text-gray-500 text-xs">${caption}</figcaption>`;
				}

				content.push(`<figure ${figureAttributes.join(' ')}>${img}</figure>`);
				return;
			}
		});

		return content.join('');
	};

	if (article.version == 2) {
		return traverse(article.content as ContentBlock[]);
	}

	if (article.version == 1) {
		let content: string;

		if (article.content instanceof Array && article.content[0]?.data?.raw) {
			content = article.content[0].data.raw as string;
		} else {
			content = article.content as string;
		}

		// Injections
		let t = 0;
		const pattern = /<\/p>/gi;

		content = content.replace(pattern, (match: string): string => {
			t++;

			if (inject[t] !== null) {
				return inject[t] ?? '';
			}

			return match;
		});

		content = content.replace(/\[pub\]/gi, '');

		return content;
	}

	return '';
};
